/**
 * Небольшой набор примитивов. Оформление задано токенами из tailwind.config.ts:
 * изумрудный акцент фирменного стиля, шампань — только в логотипе, семантика
 * статусов вынесена отдельными цветами и с акцентом не смешивается.
 */
import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  ButtonHTMLAttributes,
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import type { RequestStatus } from '@/lib/api-types';
import { formatAmd } from '@/lib/format';

/* --------------------------------- Button ---------------------------------- */

/**
 * Варианты кнопок.
 *  · `primary`  — на светлом: изумруд со светлым текстом;
 *  · `onDark`   — на тёмной плоскости: светлая кнопка с изумрудным текстом;
 *  · остальные — вспомогательные.
 * При наведении меняется заливка, при нажатии — масштаб (`.btn-fill`).
 */
type ButtonVariant = 'primary' | 'onDark' | 'secondary' | 'ghost' | 'danger';

// Перенос подписи оставлен включённым намеренно: армянское «Ուղարկել հայտը
// մասնագետին» одной строкой задаёт кнопке min-content 265 px и вместе с полями
// карточки уводит страницу за 320 px. Кнопкам, которым перенос вреден
// (шапка), `whitespace-nowrap` ставится точечно.
const BUTTON_BASE =
  'btn-fill touch-target inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2 ' +
  'text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent-500 text-ink-50 [--btn-fill:#0a1f1a]',
  onDark: 'bg-ink-100 text-accent-500 [--btn-fill:#ffffff]',
  secondary: 'border border-ink-300 bg-white text-ink-800 [--btn-fill:#edf1ee]',
  ghost: 'text-accent-500 underline underline-offset-4 [--btn-fill:#edf1ee]',
  danger: 'border border-danger-500 bg-white text-danger-500 [--btn-fill:#f7eceb]',
};

/** Классы кнопки для случаев, где нужен обычный `<a>` (якорь на этой же странице). */
export function buttonClass(variant: ButtonVariant = 'primary', className = ''): string {
  return `${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${className}`;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

export function Button({ variant = 'primary', className = '', ...rest }: ButtonProps): JSX.Element {
  return <button className={`${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${className}`} {...rest} />;
}

export function ButtonLink({
  to,
  variant = 'primary',
  className = '',
  children,
}: {
  to: string;
  variant?: ButtonVariant;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Link to={to} className={`${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${className}`}>
      {children}
    </Link>
  );
}

/* --------------------------------- Page ------------------------------------ */

/**
 * Колонка содержимого прикладного экрана. Вынесена из Layout: лендинг
 * раскладывает плоскости на всю ширину сам, все остальные экраны живут
 * в общей колонке с одинаковыми полями.
 */
export function Page({
  children,
  className = '',
  width = 'wide',
  dense = false,
}: {
  children: ReactNode;
  className?: string;
  /** `prose` — узкая колонка для текстовых экранов, `form` — для форм. */
  width?: 'wide' | 'prose' | 'form';
  /**
   * Плотная вертикаль. Кабинет и админка: сметчик работает в очереди часами,
   * там нужна плотность, а не воздух.
   */
  dense?: boolean;
}): JSX.Element {
  const widths = { wide: 'max-w-6xl', prose: 'max-w-prose', form: 'max-w-xl' } as const;
  const padding = dense ? 'py-6' : 'py-8 sm:py-12';
  return (
    <div className={`mx-auto w-full ${widths[width]} px-4 ${padding} ${className}`}>{children}</div>
  );
}

/** Заголовок прикладного экрана: маркетинговая гарнитура, спокойный кегль. */
export function PageTitle({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <h1 className={`display text-3xl sm:text-4xl ${className}`}>{children}</h1>;
}

/* --------------------------------- Fields ---------------------------------- */

interface FieldShellProps {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
  className?: string;
}

/** Обёртка поля: label, подсказка и ошибка, связанные через aria. */
export function Field({
  id,
  label,
  hint,
  error,
  children,
  className = '',
}: FieldShellProps): JSX.Element {
  return (
    <div className={className}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && !error ? (
        <p className="mt-1 text-sm text-ink-500" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function describedBy(id: string, hint?: string, error?: string): string | undefined {
  const parts: string[] = [];
  if (hint && !error) parts.push(`${id}-hint`);
  if (error) parts.push(`${id}-error`);
  return parts.length ? parts.join(' ') : undefined;
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className = '', ...rest }, ref) {
    return <input ref={ref} className={`field-control ${className}`} {...rest} />;
  },
);

/**
 * Глаз и перечёркнутый глаз для переключателя видимости пароля.
 *
 * Инлайновый SVG в том же наборе параметров, что и остальные знаки интерфейса
 * (`Icon.tsx`): штрих `currentColor` толщиной 1.6, круглые концы. Иконочной
 * библиотеки в проекте нет намеренно — один знак не стоит зависимости, а эмодзи
 * в разных системах выглядит по-разному и читается программой чтения как текст.
 * Знак декоративный (`aria-hidden`): состояние объявляет сама кнопка — именем
 * и `aria-pressed`.
 */
function EyeGlyph({ crossed }: { crossed: boolean }): JSX.Element {
  return (
    <svg
      className="h-5 w-5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3.2" />
      {crossed ? <path d="M4 20 20 4" /> : null}
    </svg>
  );
}

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Доступное имя кнопки, пока пароль скрыт («Показать пароль»). */
  showLabel: string;
  /** Доступное имя кнопки, пока пароль виден («Скрыть пароль»). */
  hideLabel: string;
}

/**
 * Поле пароля с переключателем видимости внутри поля.
 *
 * Решения, от которых зависит работоспособность:
 *  · переключатель — настоящий `<button type="button">`: внутри формы кнопка
 *    по умолчанию отправляет её, и нажатие «посмотреть пароль» уходило бы
 *    попыткой входа;
 *  · меняется атрибут `type` у ТОГО ЖЕ элемента, поле не пересоздаётся и не
 *    подменяется вторым: `autocomplete`, `name` и сам узел остаются на месте,
 *    поэтому автозаполнение браузера и менеджеры паролей продолжают работать;
 *  · состояние живёт в самом поле (`useState`), а не в сторе страницы: у двух
 *    полей на одном экране оно своё, а переход на другую страницу размонтирует
 *    компонент — показанный пароль не «утекает» на следующий экран;
 *  · доступное имя меняется вместе с состоянием, а `aria-pressed` объявляет
 *    само состояние: программе чтения не нужно догадываться по иконке.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className = '', showLabel, hideLabel, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    const label = visible ? hideLabel : showLabel;

    return (
      <div className="relative">
        <input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={`field-control pr-14 ${className}`}
          {...rest}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setVisible((value) => !value)}
          aria-pressed={visible}
          aria-label={label}
          title={label}
        >
          <EyeGlyph crossed={visible} />
        </button>
      </div>
    );
  },
);

/** Поддерживает ли браузер настраиваемый список (`appearance: base-select`). */
function supportsCustomizableSelect(): boolean {
  // jsdom объявляет CSS без `supports` — проверяем сам метод, а не объект.
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('appearance', 'base-select')
    : false;
}

/** Подпись выбранного варианта — ровно та строка, которую React только что отрисовал. */
function selectedLabelOf(node: HTMLSelectElement | null): string {
  return node?.selectedOptions[0]?.textContent?.trim() ?? '';
}

/**
 * Список выбора.
 *
 * В браузере с настраиваемым списком (Open UI, `appearance: base-select`)
 * закрытое состояние рисует первый дочерний `<button>`: только ему можно
 * задать `text-overflow: ellipsis`, кнопку, которую браузер рисует сам,
 * селектором не достать. Без этого длинная армянская подпись
 * «Ամբողջական վերանորոգում» переносилась на вторую строку, поле становилось
 * на 24 px выше соседнего в ряду, и сетка калькулятора съезжала.
 *
 * Содержимое кнопки — наш собственный узел, а не `<selectedcontent>`.
 * `<selectedcontent>` браузер наполняет копией выбранного `<option>` сам и
 * обновляет её при смене выбора, но не при смене текста самого варианта:
 * на переключении языка React меняет подписи `<option>`, выбор остаётся
 * прежним, и в закрытом поле оставалась подпись прошлой локали до
 * перезагрузки. Подпись здесь берётся из выбранного `<option>` после каждого
 * коммита — то есть из того же дерева и той же отрисовки, что и сам список
 * вариантов, — и оттуда же идёт в `title`. Разойтись с локалью ей негде:
 * ничьего чужого обновления она не ждёт.
 *
 * Сама кнопка создаётся вручную и только там, где `base-select` поддержан:
 * в остальных браузерах она не нужна, а React о таком вложении не знает и на
 * каждый список писал бы в консоль предупреждение. Наполняет её портал —
 * содержимое кнопки остаётся за React. Список `<option>` тоже за React:
 * кнопка встаёт перед ним и на согласование не влияет.
 *
 * `title` — полная подпись выбранного варианта: в закрытом состоянии текст
 * обрезан, и подсказка остаётся способом прочитать его целиком, не открывая
 * список.
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = '', title, onChange, ...rest }, ref) {
    const element = useRef<HTMLSelectElement | null>(null);
    const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
    const [selectedLabel, setSelectedLabel] = useState('');

    const attach = useCallback(
      (node: HTMLSelectElement | null) => {
        element.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    useLayoutEffect(() => {
      const node = element.current;
      if (!node || !supportsCustomizableSelect()) return;
      const existing = node.querySelector<HTMLButtonElement>(':scope > button');
      if (existing) {
        setTrigger(existing);
        return;
      }
      const button = document.createElement('button');
      button.type = 'button';
      // `tabindex="-1"`: фокус остаётся на самом списке, кнопка — только
      // поверхность отрисовки закрытого состояния.
      button.tabIndex = -1;
      node.insertBefore(button, node.firstChild);
      setTrigger(button);
    }, []);

    // Без списка зависимостей намеренно: подпись меняется и от выбора, и от
    // смены языка, а эффект после коммита видит уже перерисованные `<option>`.
    // Повторный `setState` тем же значением React отбрасывает, так что цикла
    // не возникает.
    useLayoutEffect(() => {
      setSelectedLabel(selectedLabelOf(element.current));
    });

    // Выбор варианта не обязан перерисовывать владельца формы: поля
    // калькулятора неуправляемые (react-hook-form), и коммита, после которого
    // сработал бы эффект выше, может не быть вовсе.
    const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
      setSelectedLabel(selectedLabelOf(event.currentTarget));
      onChange?.(event);
    };

    return (
      <>
        <select
          ref={attach}
          className={`field-control ${className}`}
          title={title ?? (selectedLabel || undefined)}
          onChange={handleChange}
          {...rest}
        />
        {trigger
          ? createPortal(<span className="select-value">{selectedLabel}</span>, trigger)
          : null}
      </>
    );
  },
);

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextArea({ className = '', ...rest }, ref) {
  return <textarea ref={ref} className={`field-control ${className}`} rows={4} {...rest} />;
});

/* ---------------------------------- Alert ---------------------------------- */

type AlertTone = 'info' | 'warning' | 'success' | 'danger';

const ALERT_TONES: Record<AlertTone, string> = {
  info: 'border-l-accent-500 bg-accent-50 text-accent-900',
  warning: 'border-l-amber-500 bg-amber-50 text-amber-700',
  success: 'border-l-success-500 bg-success-50 text-success-600',
  danger: 'border-l-danger-500 bg-danger-50 text-danger-600',
};

export function Alert({
  tone = 'info',
  title,
  children,
  className = '',
}: {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={`user-text rounded-xl border border-ink-200 border-l-[3px] px-4 py-3 text-sm ${ALERT_TONES[tone]} ${className}`}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? <div className={title ? 'mt-1' : ''}>{children}</div> : null}
    </div>
  );
}

/* ------------------------------- StatusBadge -------------------------------- */

const STATUS_TONES: Record<RequestStatus, string> = {
  NEW: 'bg-ink-100 text-ink-700 border-ink-300',
  IN_PROGRESS: 'bg-accent-50 text-accent-700 border-accent-200',
  NEEDS_INFO: 'bg-amber-50 text-amber-700 border-amber-100',
  QUOTE_READY: 'bg-accent-500 text-ink-50 border-accent-500',
  ACCEPTED: 'bg-success-50 text-success-600 border-success-100',
  REJECTED: 'bg-danger-50 text-danger-600 border-danger-100',
};

export function StatusBadge({
  status,
  label,
}: {
  status: RequestStatus;
  label: string;
}): JSX.Element {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1 text-sm font-medium ${STATUS_TONES[status]}`}
    >
      {label}
    </span>
  );
}

/* ---------------------------------- Money ---------------------------------- */

/** Сумма табличными цифрами: разряды не прыгают при пересчёте. */
export function Money({
  value,
  className = '',
}: {
  value: number;
  className?: string;
}): JSX.Element {
  return <span className={`tnum whitespace-nowrap ${className}`}>{formatAmd(value)}</span>;
}

/* -------------------------------- Structure -------------------------------- */

export function Section({
  title,
  description,
  children,
  className = '',
  id,
}: {
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
  id?: string;
}): JSX.Element {
  return (
    <section className={className} id={id}>
      {title ? <h2 className="text-lg font-semibold sm:text-xl">{title}</h2> : null}
      {description ? <p className="mt-1 max-w-prose text-sm text-ink-600">{description}</p> : null}
      <div className={title || description ? 'mt-4' : ''}>{children}</div>
    </section>
  );
}

/** Пара «поле — значение» для карточек параметров. */
export function DataRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-ink-100 py-2 last:border-b-0">
      <dt className="user-text min-w-0 text-sm text-ink-600">{label}</dt>
      <dd className="user-text min-w-0 text-right text-sm font-medium text-ink-900">{value}</dd>
    </div>
  );
}

export function Spinner({ label }: { label: string }): JSX.Element {
  return (
    <p className="py-8 text-center text-sm text-ink-500" role="status">
      {label}
    </p>
  );
}
